import React, { useMemo, useState } from 'react';

import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  ModalFooter,
  Button,
  Text,
  Stack,
  Box,
  Divider,
} from '@chakra-ui/react';

import { ModalRootProps } from 'components/Modal/Root';

type Totals = {
  inputs?: number;
  services?: number;
};

interface ModalSiengeImportSummaryProps extends Omit<ModalRootProps, 'data'> {
  data: {
    tableLabel: string;
    versionLabel?: string;
    totals: Totals;
  };
}

const ModalSiengeImportSummary: React.FC<ModalSiengeImportSummaryProps> = ({
  data,
  onConfirm,
  handleClose,
  ...restProps
}) => {
  const [isConfirming, setIsConfirming] = useState(false);

  const totalInputs = useMemo(() => data.totals?.inputs ?? 0, [data.totals]);
  const totalServices = useMemo(
    () => data.totals?.services ?? 0,
    [data.totals],
  );

  const handleConfirm = async (): Promise<void> => {
    if (!onConfirm) {
      handleClose();
      return;
    }

    try {
      setIsConfirming(true);
      await onConfirm();
    } catch (err) {
      setIsConfirming(false);
    } finally {
      handleClose();
    }
  };

  return (
    <Modal {...restProps} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>Resumo da importação</ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <Stack spacing={5}>
            {data.versionLabel && (
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Referência no eCustos
                </Text>
                <Text fontSize="lg" fontWeight="semibold">
                  {data.versionLabel || '-'}
                </Text>
              </Box>
            )}

            {data.tableLabel && (
              <Box>
                <Text fontSize="sm" color="gray.500">
                  Tabela do Sienge Plataforma
                </Text>
                <Text fontSize="lg" fontWeight="semibold">
                  {data.tableLabel}
                </Text>
              </Box>
            )}

            {(data.versionLabel || data.tableLabel) && <Divider />}

            <Stack spacing={3}>
              <Text>
                Deseja importar automaticamente a tabela do Sienge Plataforma?
              </Text>

              <Text>Esse processo pode demorar um pouco.</Text>

              <Box
                borderWidth="1px"
                borderColor="gray.200"
                borderRadius="md"
                p={4}
              >
                <Stack spacing={3}>
                  <Box>
                    <Text fontSize="sm" color="gray.500">
                      Vão ser importados
                    </Text>
                    <Text fontSize="2xl" fontWeight="semibold">
                      {totalInputs.toLocaleString('pt-BR')} insumos
                    </Text>
                  </Box>

                  <Divider />

                  <Box>
                    <Text fontSize="2xl" fontWeight="semibold">
                      {totalServices.toLocaleString('pt-BR')} serviços
                    </Text>
                  </Box>
                </Stack>
              </Box>
            </Stack>
          </Stack>
        </ModalBody>

        <ModalFooter>
          <Button onClick={handleClose}>Cancelar</Button>
          <Button
            ml={3}
            colorScheme="green"
            onClick={handleConfirm}
            isLoading={isConfirming}
          >
            Confirmar importação
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ModalSiengeImportSummary;
